/**
 * Contract period arithmetic (TD-2812).
 *
 * Kept out of the components so the admin panel, the period form and the
 * customer-facing counters all read a boundary the same way, and so the rule can
 * be tested without rendering anything.
 */

/**
 * Whether the backend counts downloads per contract period.
 *
 * It does since TD-2744: a download is recorded against the period it was made
 * in, the count and the de-duplication are per period, and a company without
 * periods stays on lifetime counting. Tokens and credits were period-scoped
 * before that (TD-2808).
 *
 * Kept as a named switch so every string that depends on it is found from one
 * place. Only ever set it back to false together with reverting that backend
 * change: telling a customer their allowance resets, or labelling a figure
 * "this period", is false over a lifetime count.
 */
export const DOWNLOADS_ARE_PERIOD_SCOPED = true;

/**
 * Calendar day from an API date value (`YYYY-MM-DD` or a full ISO timestamp).
 *
 * Data API DATE columns serialise as `2027-04-01T00:00:00.000Z`. HTML date
 * inputs and the length preview only accept `YYYY-MM-DD`, so every consumer
 * strips the time part the same way.
 */
export const isoDay = (value) => String(value || '').split('T')[0];

/**
 * A contract date as `DD.MM.YYYY`.
 *
 * Deliberately not `formatDateDDMMYYYY` from utils: that parses the value with
 * `new Date()` and reads it back with local getters, which shifts a date-only
 * string by a day for anyone west of UTC. A period boundary is the one date in
 * the app where being a day out changes what a customer is allowed to do, so
 * this splits the string instead of parsing it.
 */
export const formatPeriodDate = (value) => {
  if (!value) return '—';
  const [year, month, day] = isoDay(value).split('-');
  if (!year || !month || !day) return '—';
  return `${day}.${month}.${year}`;
};

/**
 * End date from a start plus a length, mirroring the backend's rule so what an
 * admin is shown is what gets stored.
 *
 * Free months are granted at the START of a period and EXTEND it: 12 paid plus
 * 3 free is one 15-month term carrying the year's allowance, not a 12-month term
 * with a tail. The end is inclusive: the day before the start's day of month in
 * the target month, or that month's last day when it has no such day. 31 January
 * plus one month ends 28 February and the next period starts 1 March.
 */
export const previewEndsOn = (startsOn, lengthMonths, freeMonths) => {
  if (!startsOn || !lengthMonths) return '';
  const months = Number(lengthMonths) + Math.floor(Number(freeMonths) || 0);
  const [year, month, day] = isoDay(startsOn).split('-').map(Number);
  if (!year || !month || !day) return '';

  const targetMonth = month - 1 + months;
  const daysInTarget = new Date(Date.UTC(year, targetMonth + 1, 0)).getUTCDate();
  const end =
    day > daysInTarget
      ? new Date(Date.UTC(year, targetMonth, daysInTarget))
      : new Date(Date.UTC(year, targetMonth, day - 1));
  return end.toISOString().split('T')[0];
};

/**
 * How many days a period covers, for the timeline's proportions. Approximate on
 * purpose: this decides how wide a bar is, not what anybody is allowed.
 */
export const spanInDays = (period, fallbackEnd) => {
  const start = new Date(`${String(period.starts_on).split('T')[0]}T00:00:00Z`);
  const endRaw = period.ends_on || fallbackEnd;
  if (!endRaw) return 365;
  const end = new Date(`${String(endRaw).split('T')[0]}T00:00:00Z`);
  return Math.max(1, Math.round((end - start) / 86400000) + 1);
};
